import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyExpenseAPI } from '../../services/api';
import * as XLSX from 'xlsx';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import './PropertyExpenseList.css';

const PropertyExpenseList = () => {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const fileInputRef = React.useRef(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summaryData, setSummaryData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    loadExpenses();
    if (dateFrom || dateTo) {
      loadSummary();
    } else {
      setSummaryData(null);
    }
  }, [page, dateFrom, dateTo]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await propertyExpenseAPI.getAll(searchTerm, page, pageSize, dateFrom || null, dateTo || null);
      setExpenses(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      setErrorMessage(t('propertyExpense.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    if (!dateFrom && !dateTo) {
      setSummaryData(null);
      return;
    }
    try {
      setLoadingSummary(true);
      const response = await propertyExpenseAPI.getSummary(dateFrom || null, dateTo || null);
      setSummaryData(response.data);
    } catch (err) {
      console.error('Error loading summary:', err);
      setSummaryData(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadExpenses();
    loadSummary();
  };

  const handleClearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await propertyExpenseAPI.delete(id);
        setSuccessMessage(t('propertyExpense.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadExpenses();
      } catch (err) {
        setErrorMessage(t('propertyExpense.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('propertyExpense.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('propertyExpense.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await propertyExpenseAPI.bulkDelete(selectedIds);
        setSuccessMessage(response.data.message || t('propertyExpense.list.messages.bulkDeleteSuccess', { count: selectedIds.length }));
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadExpenses();
      } catch (err) {
        setErrorMessage(err.response?.data?.message || t('propertyExpense.list.messages.bulkDeleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('propertyExpense.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) {
      await confirmAction();
    }
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(expenses.map(e => e.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExport = async (format) => {
    try {
      setShowExportMenu(false);
      let response;
      let contentType;

      switch (format) {
        case 'excel':
          response = await propertyExpenseAPI.exportExcel();
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await propertyExpenseAPI.exportCsv();
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await propertyExpenseAPI.exportPdf();
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setSuccessMessage(t('propertyExpense.list.messages.exportSuccess', { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t('propertyExpense.list.messages.exportError', { format: format.toUpperCase() }) + ` ${err.response?.data?.message || err.message}`);
      setShowErrorModal(true);
    }
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setErrorMessage(t('propertyExpense.list.messages.importError'));
      setShowErrorModal(true);
      return;
    }

    try {
      const fileData = await file.arrayBuffer();
      const workbook = XLSX.read(fileData, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const expensesToImport = jsonData.map(row => ({
        date: row.Date || row.date || new Date().toISOString().split('T')[0],
        expenseDescription: row['Expense Description'] || row.expenseDescription || row.ExpenseDescription || '',
        amount: parseFloat(row.Amount || row.amount || 0),
        invoiceNumber: row['Invoice Number'] || row.invoiceNumber || row.InvoiceNumber || '',
        payingEntity: row['Paying Entity'] || row.payingEntity || row.PayingEntity || ''
      })).filter(e => e.expenseDescription && e.amount > 0);

      if (expensesToImport.length === 0) {
        setErrorMessage(t('propertyExpense.list.messages.importNoData'));
        setShowErrorModal(true);
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const expense of expensesToImport) {
        try {
          await propertyExpenseAPI.create(expense);
          successCount++;
        } catch (err) {
          errorCount++;
          console.error('Error importing expense:', expense, err);
        }
      }

      if (successCount > 0) {
        if (errorCount > 0) {
          setSuccessMessage(t('propertyExpense.list.messages.importPartialSuccess', { success: successCount, error: errorCount }));
        } else {
          setSuccessMessage(t('propertyExpense.list.messages.importSuccess', { count: successCount }));
        }
        setShowSuccessModal(true);
        loadExpenses();
      } else {
        setErrorMessage(t('propertyExpense.list.messages.importFailed'));
        setShowErrorModal(true);
      }

      e.target.value = '';
    } catch (err) {
      setErrorMessage(t('propertyExpense.list.messages.importProcessError'));
      setShowErrorModal(true);
      e.target.value = '';
    }
  };

  const handlePrintReport = async () => {
    try {
      const response = await propertyExpenseAPI.printReport();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Print error:', error);
      setErrorMessage('Failed to generate PDF report. Please try again.');
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="list-view-card">
        <div className="page-actions">
        <div className="page-actions-left">
          <Link to="/property-expense/create" className="btn-add">
            + {t('propertyExpense.list.addNew')}
          </Link>
          <div className="filters-row">
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder={t('propertyExpense.list.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              />
              <button className="search-icon" onClick={handleSearch}>
                <i className="fas fa-search"></i>
              </button>
            </div>
            <div className="date-filter-container">
              <div className="date-filter-group">
                <label className="date-filter-label">
                  <i className="fas fa-calendar-alt"></i> {t('propertyExpense.list.dateFrom')}
                </label>
                <input
                  type="date"
                  className="date-filter-input"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="date-filter-group">
                <label className="date-filter-label">
                  <i className="fas fa-calendar-alt"></i> {t('propertyExpense.list.dateTo')}
                </label>
                <input
                  type="date"
                  className="date-filter-input"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              {(dateFrom || dateTo) && (
                <button className="btn-clear-filter" onClick={handleClearDateFilter} title={t('common.clear')}>
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="action-buttons">
          <button className="action-btn action-btn-print" title={t('common.printReport', 'Print Report')} onClick={handlePrintReport}>
            <i className="fas fa-print"></i>
          </button>
          <div className="export-menu-container">
            <button className="action-btn action-btn-export" title={t('common.export')} onClick={() => setShowExportMenu(!showExportMenu)}>
              <i className="fas fa-file-export"></i>
            </button>
            {showExportMenu && (
              <div className="export-menu">
                <button onClick={() => handleExport('excel')}>
                  <i className="fas fa-file-excel"></i> {t('common.exportExcel')}
                </button>
                <button onClick={() => handleExport('csv')}>
                  <i className="fas fa-file-csv"></i> {t('common.exportCsv')}
                </button>
                <button onClick={() => handleExport('pdf')}>
                  <i className="fas fa-file-pdf"></i> {t('common.exportPdf')}
                </button>
              </div>
            )}
          </div>
          <button className="action-btn action-btn-import" title={t('propertyExpense.list.import')} onClick={handleImport}>
            <i className="fas fa-file-import"></i>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <button className="action-btn action-btn-delete" title={t('propertyExpense.list.bulkDelete')} onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedIds.length === expenses.length && expenses.length > 0}
                />
              </th>
              <th>{t('propertyExpense.list.columns.id')}</th>
              <th>{t('propertyExpense.list.columns.date')}</th>
              <th>{t('propertyExpense.list.columns.expenseDescription')}</th>
              <th>{t('propertyExpense.list.columns.amount')}</th>
              <th>{t('propertyExpense.list.columns.invoiceNumber')}</th>
              <th>{t('propertyExpense.list.columns.payingEntity')}</th>
              <th>{t('propertyExpense.list.columns.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  {t('propertyExpense.list.messages.noData')}
                </td>
              </tr>
            ) : (
              expenses.map((expense) => (
                <tr key={expense.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(expense.id)}
                      onChange={() => handleSelectOne(expense.id)}
                    />
                  </td>
                  <td>{expense.id}</td>
                  <td>{new Date(expense.date).toLocaleDateString()}</td>
                  <td>{expense.expenseDescription}</td>
                  <td>{expense.amount.toFixed(2)}</td>
                  <td>{expense.invoiceNumber || '-'}</td>
                  <td>{expense.payingEntity || '-'}</td>
                  <td>
                    <div className="action-buttons-cell">
                      <Link
                        to={`/property-expense/view/${expense.id}`}
                        className="btn btn-sm btn-view"
                        title={t('common.view')}
                      >
                        <i className="fas fa-eye"></i>
                      </Link>
                      <Link
                        to={`/property-expense/edit/${expense.id}`}
                        className="btn btn-sm btn-edit"
                        title={t('common.edit')}
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                      <button
                        className="btn btn-sm btn-delete"
                        onClick={() => handleDelete(expense.id)}
                        title={t('common.delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

        {totalPages > 1 && (
          <div className="pagination-container">
            <ul className="pagination">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  {t('common.previous') || 'Previous'}
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  {t('common.next')}
                </button>
              </li>
          </ul>
        </div>
        )}

      {/* Summary/Report Container */}
      {(dateFrom || dateTo) && (
        <div className="summary-container">
          <div className="summary-header">
            <h3 className="summary-title">
              <i className="fas fa-chart-bar"></i> {t('propertyExpense.list.summary.title')}
            </h3>
            {summaryData && (
              <div className="summary-period">
                {summaryData.dateFrom && summaryData.dateTo && (
                  <span>
                    {new Date(summaryData.dateFrom).toLocaleDateString()} - {new Date(summaryData.dateTo).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}
          </div>
          
          {loadingSummary ? (
            <div className="summary-loading">
              <i className="fas fa-spinner fa-spin"></i> {t('common.loading')}
            </div>
          ) : summaryData ? (
            <div className="summary-content">
              {/* Key Metrics */}
              <div className="summary-metrics">
                <div className="summary-metric-card">
                  <div className="metric-icon" style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)', color: '#28a745' }}>
                    <i className="fas fa-list"></i>
                  </div>
                  <div className="metric-content">
                    <div className="metric-label">{t('propertyExpense.list.summary.totalRecords')}</div>
                    <div className="metric-value">{summaryData.totalCount || 0}</div>
                  </div>
                </div>
                
                <div className="summary-metric-card">
                  <div className="metric-icon" style={{ backgroundColor: 'rgba(191, 79, 31, 0.1)', color: '#bf4f1f' }}>
                    <i className="fas fa-dollar-sign"></i>
                  </div>
                  <div className="metric-content">
                    <div className="metric-label">{t('propertyExpense.list.summary.totalAmount')}</div>
                    <div className="metric-value">{summaryData.totalAmount?.toFixed(2) || '0.00'} SAR</div>
                  </div>
                </div>
                
                <div className="summary-metric-card">
                  <div className="metric-icon" style={{ backgroundColor: 'rgba(0, 123, 255, 0.1)', color: '#007bff' }}>
                    <i className="fas fa-calculator"></i>
                  </div>
                  <div className="metric-content">
                    <div className="metric-label">{t('propertyExpense.list.summary.averageAmount')}</div>
                    <div className="metric-value">{summaryData.averageAmount?.toFixed(2) || '0.00'} SAR</div>
                  </div>
                </div>
              </div>

              {/* Monthly Breakdown */}
              {summaryData.byMonth && summaryData.byMonth.length > 0 && (
                <div className="summary-section">
                  <h4 className="section-title">
                    <i className="fas fa-calendar-alt"></i> {t('propertyExpense.list.summary.monthlyBreakdown')}
                  </h4>
                  <div className="monthly-list">
                    {summaryData.byMonth.map((month) => (
                      <div key={`${month.year}-${month.month}`} className="month-item">
                        <div className="month-name">{month.monthName}</div>
                        <div className="month-stats">
                          <span className="month-count">{month.count} {t('propertyExpense.list.summary.records')}</span>
                          <span className="month-income">{month.totalAmount?.toFixed(2) || '0.00'} SAR</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Descriptions */}
              {summaryData.topDescriptions && summaryData.topDescriptions.length > 0 && (
                <div className="summary-section">
                  <h4 className="section-title">
                    <i className="fas fa-tags"></i> {t('propertyExpense.list.summary.topDescriptions')}
                  </h4>
                  <div className="properties-list">
                    {summaryData.topDescriptions.map((desc, index) => (
                      <div key={index} className="property-item">
                        <div className="property-rank">#{index + 1}</div>
                        <div className="property-info">
                          <div className="property-name">{desc.description}</div>
                          <div className="property-stats">
                            <span className="property-count">{desc.count} {t('propertyExpense.list.summary.records')}</span>
                          </div>
                        </div>
                        <div className="property-income">
                          {desc.totalAmount?.toFixed(2) || '0.00'} SAR
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="summary-empty">
              <i className="fas fa-info-circle"></i> {t('propertyExpense.list.summary.noData')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyExpenseList;



